import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { isApiError, refusalMessage } from '../api';
import { riderRatings } from '../api/ratings';
import { clearDriverSession } from '../lib/driverSession';
import './chatNotifyRating.css';

function Star({ on, onClick, label }) {
  return (
    <button type="button" className="ingRate__starB" onClick={onClick} aria-label={label} aria-pressed={on}>
      <svg viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M12 2.2l1.5 4.2h4.2l-3.4 2.6L15.8 14L12 11.5L8.2 14l1.2-3.7L5.3 7.3h4.2L10.8 3.1z"
          stroke="#ec6c23"
          strokeWidth="1.1"
          strokeLinejoin="round"
          fill={on ? '#ec6c23' : 'none'}
        />
      </svg>
    </button>
  );
}

/** The delivery's id: the job the cash screen handed over, or the route's `deliveries:<id>`. */
function deliveryIdOf(order, jobKey) {
  if (order?.deliveryId) return String(order.deliveryId);
  const [table, id] = String(jobKey || '').split(':');

  return table === 'deliveries' && id ? id : '';
}

const MAX_CH = 250;
const HOME = '/driver/home';

/**
 * The rider rates the client after a delivery (ingo-e0, 2 Oct 2026; InGo ADR
 * 0029), straight after "Payment collected": one to five stars and a note for
 * the office, which the client never sees. Once per delivery, never changed;
 * a second try (the retry after a lost answer) is read as sent. "Skip" goes
 * home without rating.
 */
export default function DriverRateCustomerPage({ ratings = riderRatings }) {
  const navigate = useNavigate();
  const { state = {} } = useLocation();
  const { jobKey } = useParams();
  const deliveryId = useMemo(() => deliveryIdOf(state?.order, jobKey), [state, jobKey]);
  const [about, setAbout] = useState(null);
  const [rating, setRating] = useState(0);
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  const home = (toast) => navigate(HOME, { replace: true, state: toast ? { toast } : undefined });

  useEffect(() => {
    if (!deliveryId) {
      navigate(HOME, { replace: true });
      return undefined;
    }
    let cancelled = false;
    ratings.state(deliveryId).then(
      (answer) => !cancelled && setAbout(answer),
      (error) => {
        if (cancelled) return;
        if (isApiError(error) && error.status === 401) {
          clearDriverSession();
          navigate('/driver/login', { replace: true });
          return;
        }
        setErr(refusalMessage(error));
      },
    );

    return () => {
      cancelled = true;
    };
  }, [deliveryId, ratings, navigate]);

  const submit = async () => {
    setErr('');
    if (rating < 1 || rating > 5) {
      setErr('Choose from one to five stars.');
      return;
    }
    setBusy(true);
    try {
      await ratings.rate(deliveryId, { stars: rating, comment: text });
      home('Thank you. Your rating is recorded.');
    } catch (error) {
      if (isApiError(error) && error.code === 'ALREADY_RATED') {
        home('Thank you. Your rating is recorded.');
        return;
      }
      setErr(refusalMessage(error));
    } finally {
      setBusy(false);
    }
  };

  const given = about?.rating ?? null;
  const closed = about !== null && !about.may_rate;

  return (
    <div className="ingRate drvRate" role="main" aria-label="Rate customer">
      <div className="drvRate__wrap">
        <div className="drvRate__card">
          <h1 className="drvRate__title">Rate Customer</h1>
          <p className="drvRate__sub">How was the delivery for you?</p>
          <div className="drvRate__meta">
            <p className="drvRate__name">{about?.name || state?.order?.customerName || 'Customer'}</p>
            {about?.delivery?.reference ? <p className="drvRate__order">{about.delivery.reference}</p> : null}
          </div>

          {closed ? (
            <>
              <p className="ingRate__sub" role="status">
                {given ? `You rated this customer ${given.stars} / 5. A rating cannot be changed.` : about.refusal}
              </p>
              <button type="button" className="ingRate__submit" onClick={() => home()}>
                Back to home
              </button>
            </>
          ) : (
            <>
              <h2 className="ingRate__secT">How was the customer?</h2>
              <div className="ingRate__stars" role="group" aria-label="Customer rating">
                {[1, 2, 3, 4, 5].map((i) => (
                  <Star key={i} on={i <= rating} onClick={() => setRating(i)} label={`${i} ${i === 1 ? 'star' : 'stars'}`} />
                ))}
              </div>
              <p className="ingRate__sub" style={{ minHeight: '1rem' }}>
                {rating > 0 ? `${rating} / 5` : 'Tap stars to rate'}
              </p>

              <h2 className="ingRate__secT">A note for the office</h2>
              <textarea
                className="ingRate__ta"
                rows={4}
                maxLength={MAX_CH}
                value={text}
                onChange={(e) => setText(e.target.value.slice(0, MAX_CH))}
                placeholder="Only the office reads this."
                aria-label="A note for the office"
              />
              <div className="ingRate__taC">
                {text.length}/{MAX_CH}
              </div>
              {err ? (
                <p className="auth-message auth-message--error" role="alert">
                  {err}
                </p>
              ) : null}
              <button type="button" className="ingRate__submit" disabled={busy || about === null} onClick={submit}>
                {busy ? 'Submitting...' : 'Submit Review'}
              </button>
              <button type="button" className="ingRate__skip" onClick={() => home()}>
                Skip for now
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
